import { Link, NavLink, useLocation } from 'react-router-dom';
import {
    HiHome,
    HiUser,
    HiClipboardCheck,
    HiDocumentSearch,
    HiCalendar,
    HiCreditCard,
    HiLogout,
    HiX,
    HiBadgeCheck,
    HiLocationMarker,
    HiDownload,
    HiBookmark,
    HiPhotograph,
    HiMail,
    HiUserGroup,
    HiShieldCheck,
    HiAcademicCap,
    HiOutlineSparkles,
    HiCube,
    HiChartBar
} from 'react-icons/hi';
import useAuthStore from '@store/authStore';

const DashboardSidebar = ({ isOpen, onClose }) => {
    const location = useLocation();
    const { user, logout } = useAuthStore();

    const navigation = [
        { name: 'Dashboard', href: '/dashboard', icon: HiHome, end: true },
        { name: 'Messages', href: '/dashboard/messages', icon: HiMail },
        { name: 'Payment Methods', href: '/dashboard/payment-methods', icon: HiCreditCard },
        { name: 'Account Details', href: '/dashboard/profile', icon: HiUser },
    ];

    if (user?.role !== 'admin') {
        navigation.splice(1, 0, { name: 'Addresses', href: '/dashboard/addresses', icon: HiLocationMarker });
    }

    if (user?.role === 'studio') {
        navigation.splice(1, 0, { name: 'Saved Vendors', href: '/dashboard/saved-vendors', icon: HiBookmark });
    }

    // Candidate/Certified Vendor specific items
    if (['candidate', 'certified_vendor'].includes(user?.role)) {
        navigation.push(
            { name: 'Pre-Assessment', href: '/dashboard/pre-assessment', icon: HiClipboardCheck }
        );
    }

    if (['candidate', 'certified_vendor'].includes(user?.role)) {
        navigation.push(
            { name: 'My Audits', href: '/dashboard/audits', icon: HiDocumentSearch },
            { name: 'My Portfolio', href: '/dashboard/portfolio', icon: HiPhotograph }
        );
    }

    // Shared items continued
    navigation.push(
        { name: 'Downloads', href: '/dashboard/downloads', icon: HiDownload }
    );

    // Workshops & Training (all non-admin roles)
    if (user?.role !== 'admin') {
        navigation.push(
            { name: 'Workshops & Training', href: '/dashboard/workshops', icon: HiAcademicCap }
        );
    }

    // Membership & Plans (all non-admin, non-assessor roles)
    if (user?.role !== 'admin' && user?.role !== 'assessor') {
        navigation.push(
            { name: 'Membership & Plans', href: '/dashboard/subscription', icon: HiOutlineSparkles }
        );
    }

    // Directory access for specific roles
    if (['admin', 'candidate', 'certified_vendor', 'studio'].includes(user?.role)) {
        navigation.push(
            { name: 'Assessor Directory', href: '/dashboard/assessors-directory', icon: HiUserGroup }
        );
    }

    // Admin-specific items
    if (user?.role === 'assessor') {
        navigation.push(
            { name: 'Assessor Workspace', href: '/dashboard/assessor', icon: HiClipboardCheck }
        );
    }

    if (user?.role === 'admin') {
        navigation.push(
            { name: 'User Management', href: '/dashboard/admin/users', icon: HiUser },
            { name: 'Pre-Assessment Review', href: '/dashboard/admin/pre-assessments', icon: HiClipboardCheck },
            { name: 'All Audits', href: '/dashboard/audits', icon: HiDocumentSearch },
            { name: 'Workshops Mgmt.', href: '/dashboard/admin/workshops', icon: HiAcademicCap },
            { name: 'Product Mgmt.', href: '/dashboard/admin/products', icon: HiCube },
            { name: 'Benchmark Mgmt.', href: '/dashboard/admin/benchmarks', icon: HiChartBar },
            { name: 'Assessor Recruitment', href: '/dashboard/admin/assessor-recruitments', icon: HiUserGroup }
        );
    }

    const handleLogout = async () => {
        await logout();
    };



    return (
        <>
            {/* Mobile overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={onClose}
                />
            )}

            {/* Sidebar */}
            <aside className={`
        fixed top-[116px] left-0 z-40 h-[calc(100vh-116px)] w-64 bg-white shadow-xl transform transition-transform duration-300
        lg:translate-x-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
                {/* User info */}
                <div className="px-6 py-4 border-b border-secondary-200 bg-secondary-50">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center overflow-hidden border border-secondary-100">
                            <img src="/logos/VOE_White_Square_transparent.png" alt="VOE" className="w-8 h-8 object-contain" />
                        </div>
                        <div>
                            <p className="font-bold text-secondary-900 truncate max-w-[140px] leading-tight">{user?.firstName}</p>
                            <p className="text-[10px] font-bold text-gold-600 uppercase tracking-wider">{user?.role?.replace('_', ' ')}</p>
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="p-4 space-y-1 overflow-y-auto h-full pb-20">
                    {navigation.map((item) => (
                        <NavLink
                            key={item.name}
                            to={item.href}
                            end={item.end}
                            onClick={onClose}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-bold transition-colors ${isActive
                                    ? 'bg-gold-50 text-gold-700 border-r-4 border-gold-500 rounded-r-none'
                                    : 'text-secondary-700 hover:bg-secondary-50 hover:text-gold-600'
                                }`
                            }
                        >
                            <item.icon className="w-5 h-5" />
                            {item.name}
                        </NavLink>
                    ))}

                    <div className="pt-4 mt-4 border-t border-secondary-200">
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                        >
                            <HiLogout className="w-5 h-5" />
                            Logout
                        </button>
                    </div>
                </nav>
            </aside>
        </>
    );
};

export default DashboardSidebar;
